import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Badge, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, PageHeader, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useNow } from '../../inbox/components/SlaBadge';
import { TICKETS_KEY, ticketErrorText, useLookups } from '../lib/data';
import { DoneModal, taskKindLabel } from '../view/Tasks';
import '../tickets.css';

type Whose = 'mine' | 'everyone';
type Which = 'open' | 'all';

export default function TicketTasksPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const now = useNow(30_000);
  const lk = useLookups();
  const [whose, setWhose] = usePersistedState<Whose>('tenant.tickets.tasks.whose', 'mine');
  const [which, setWhich] = usePersistedState<Which>('tenant.tickets.tasks.which', 'open');
  const [finishing, setFinishing] = useState<tickets.Task | null>(null);

  const q = useQuery({
    queryKey: [TICKETS_KEY, 'tasks', whose, which],
    queryFn: () => tickets.listTasks({ mine: whose === 'mine' ? true : undefined, open: which === 'open' ? true : undefined }),
    refetchInterval: 60_000
  });

  // Open ones by due time (none last), then the done ones, newest first.
  const rows = useMemo(() => [...(q.data?.data ?? [])].sort((a, b) => {
    if (!!a.done_at !== !!b.done_at) return a.done_at ? 1 : -1;
    if (a.done_at && b.done_at) return b.done_at.localeCompare(a.done_at);
    return (a.due_at ?? '9').localeCompare(b.due_at ?? '9');
  }) as Indexed<tickets.Task>[], [q.data]);
  const overdue = rows.filter((x) => !x.done_at && x.due_at && Date.parse(x.due_at) < now).length;

  return (
    <Stack gap="lg">
      <PageHeader title={t('tickets.myTasks.title')} description={t('tickets.myTasks.description')} withDivider={false}
        actions={<HelpButton guide="tickets" />} />

      <Group gap="sm" wrap="wrap" data-guide="tickets-tasks">
        <SegmentedControl size="sm" value={whose} onChange={(v) => setWhose(v as Whose)}
          data={[{ value: 'mine', label: t('tickets.myTasks.mine') }, { value: 'everyone', label: t('tickets.myTasks.everyone') }]} />
        <SegmentedControl size="sm" value={which} onChange={(v) => setWhich(v as Which)}
          data={[{ value: 'open', label: t('tickets.myTasks.open') }, { value: 'all', label: t('tickets.myTasks.all') }]} />
        {overdue > 0 && <Badge color="red" variant="light">{t('tickets.myTasks.overdueCount', { count: overdue })}</Badge>}
      </Group>

      <TableShell
        loading={q.isPending}
        error={q.error ? (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{ticketErrorText(t, q.error)}</span>
              <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={!q.isPending && rows.length === 0}
        empty={<EmptyState title={t('tickets.myTasks.empty')} description={t('tickets.myTasks.emptyHint')} />}
      >
        <DataTable<Indexed<tickets.Task>>
          records={rows}
          idAccessor="id"
          onRowClick={(x) => nav(`/tickets/${encodeURIComponent(x.ticket_id)}`)}
          columns={[
            {
              accessor: 'due_at',
              title: t('tickets.task.due'),
              render: (x) => {
                const late = !x.done_at && !!x.due_at && Date.parse(x.due_at) < now;
                return (
                  <Text role="bodySmall" fw={late ? 600 : undefined} c={late ? 'var(--rr-color-error)' : undefined} title={x.due_at ? formatDateTime(x.due_at) : undefined}>
                    {x.due_at ? (late ? t('tickets.task.overdueSince', { when: formatRelative(x.due_at) }) : formatDateTime(x.due_at)) : t('tickets.task.noDue')}
                  </Text>
                );
              }
            },
            {
              accessor: 'title',
              title: t('tickets.task.title'),
              render: (x) => (
                <Stack gap={2}>
                  <Group gap="xs" wrap="nowrap">
                    <Badge size="xs" variant="light" color={x.done_at ? 'gray' : undefined}>{taskKindLabel(t, x.kind)}</Badge>
                    <Text role="bodySmall" fw={500} style={x.done_at ? { textDecoration: 'line-through' } : undefined}>{x.title}</Text>
                  </Group>
                  {x.question && <Text role="caption" c="var(--rr-color-text-muted)" lineClamp={1}>{x.question}</Text>}
                </Stack>
              )
            },
            {
              accessor: 'ticket_number',
              title: t('tickets.myTasks.ticket'),
              render: (x) => (
                <Stack gap={0}>
                  <Text role="bodySmall" fw={600}>{x.ticket_number ?? ''}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)" lineClamp={1}>{x.ticket_subject ?? ''}</Text>
                </Stack>
              )
            },
            ...(whose === 'everyone' ? [{
              accessor: 'assignee_user_id',
              title: t('tickets.field.assignee'),
              render: (x: Indexed<tickets.Task>) => (
                <Text role="bodySmall">{x.assignee_user_id ? (x.assignee_user_id === user?.id ? t('tickets.me') : lk.personLabel(x.assignee_user_id) || t('common.unknown')) : t('tickets.unassigned')}</Text>
              )
            }] : []),
            {
              accessor: 'actions',
              title: t('common.actions'),
              render: (x) => x.done_at
                ? <Text role="caption" c="var(--rr-color-text-muted)">{x.outcome || t('tickets.myTasks.done')}</Text>
                : (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Button size="xs" variant="default" onClick={() => setFinishing(x)}>{t('tickets.task.markDone')}</Button>
                  </span>
                )
            }
          ]}
        />
      </TableShell>

      <DoneModal
        task={finishing}
        ticketId={finishing?.ticket_id ?? ''}
        onClose={() => setFinishing(null)}
        onDone={() => void qc.invalidateQueries({ queryKey: [TICKETS_KEY] })}
      />
    </Stack>
  );
}
